import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Clock, User, Building } from 'lucide-react';
import { ordersAPI } from '../../lib/api.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { formatDate, formatStatus } from '../../utils/formatters.js';

function OrderDetailPage() {
  const { id } = useParams();

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', id],
    queryFn: () => ordersAPI.getById(id),
  });

  const orderData = order?.data;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        </div>
        <Card>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 dark:text-gray-400">
          Order not found.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button variant="secondary" size="sm">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Orders
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {orderData.title}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Order #{orderData.id}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Order Details
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description
                </h3>
                <p className="text-gray-900 dark:text-white mt-1">
                  {orderData.description || 'No description provided'}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Status
                  </h3>
                  <Badge variant="warning" className="mt-1">
                    {formatStatus(orderData.status)}
                  </Badge>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Priority
                  </h3>
                  <p className="text-gray-900 dark:text-white mt-1">
                    {orderData.priority || 'Normal'}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Timeline
            </h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-brand-primary rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Order Created
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(orderData.createdAt)}
                  </p>
                </div>
              </div>
              {orderData.updatedAt && (
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-brand-accent rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Last Updated
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(orderData.updatedAt)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Order Information
            </h2>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Building className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {orderData.company?.name || 'No company'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Company
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <User className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {orderData.assignee?.name || 'Unassigned'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Assigned To
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Clock className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatDate(orderData.dueDate)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Due Date
                  </p>
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Actions
            </h2>
            <div className="space-y-3">
              <Button variant="primary" className="w-full">
                Approve Order
              </Button>
              <Button variant="outline" className="w-full">
                Reject Order
              </Button>
              <Button variant="secondary" className="w-full">
                Edit Order
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default OrderDetailPage;
